import React from 'react';
import styled from 'styled-components';
import { BarChart3, TrendingUp, Users, DollarSign } from 'lucide-react';
import { useQuery } from 'react-query';
import { adminAPI } from '../services/api';
import toast from 'react-hot-toast';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.lg};
`;

const Header = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const StatCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing.xl};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
`;

const StatIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: ${props => props.theme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  background: ${props => props.color || props.theme.colors.primary};
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const StatLabel = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-weight: 500;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xl};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing.xl};
`;

const ChartTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const LoadingState = styled.div`
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
`;

const EmptyState = styled.div`
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
`;

function Analytics() {
  // Get analytics data from Dashboard stats
  const { data: dashboardData, isLoading } = useQuery(
    'dashboardStats',
    adminAPI.getDashboardStats,
    {
      refetchInterval: 60000,
      onError: (err) => {
        toast.error('Не удалось загрузить данные');
        console.error('Analytics error:', err);
      }
    }
  );

  // Prepare chart data
  const revenueData = React.useMemo(() => {
    if (!dashboardData) return [];
    
    // Mock monthly revenue data (in real implementation, this would come from API)
    const months = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];
    return months.map((month, idx) => ({
      month,
      revenue: Math.floor(dashboardData.monthlyRevenue || 0) / 12 * (idx + 1)
    }));
  }, [dashboardData]);

  const userData = React.useMemo(() => {
    if (!dashboardData) return [];
    
    const months = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];
    return months.map((month, idx) => ({
      month,
      users: Math.floor(dashboardData.totalUsers || 0) / 12 * (idx + 1)
    }));
  }, [dashboardData]);

  const stats = React.useMemo(() => {
    if (!dashboardData) return [];
    
    return [
      {
        icon: Users,
        label: 'Всего пользователей',
        value: dashboardData.totalUsers?.toLocaleString('ru-RU') || '0',
        color: '#3b82f6'
      },
      {
        icon: DollarSign,
        label: 'Доход за месяц',
        value: `₽${(dashboardData.monthlyRevenue || 0).toLocaleString('ru-RU')}`,
        color: '#10b981'
      },
      {
        icon: TrendingUp,
        label: 'Активных задач',
        value: dashboardData.activeJobs?.toString() || '0',
        color: '#f59e0b'
      },
      {
        icon: Users,
        label: 'Рефералов',
        value: dashboardData.referrals?.toString() || '0',
        color: '#8b5cf6'
      }
    ];
  }, [dashboardData]);

  if (isLoading) {
    return (
      <Container>
        <Header>
          <Title>
            <BarChart3 size={32} />
            Аналитика
          </Title>
        </Header>
        <LoadingState>Загрузка аналитики...</LoadingState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          <BarChart3 size={32} />
          Аналитика
        </Title>
      </Header>

      <StatsGrid>
        {stats.map((stat, index) => (
          <StatCard key={index}>
            <StatIcon color={stat.color}>
              <stat.icon size={24} />
            </StatIcon>
            <StatContent>
              <StatValue>{stat.value}</StatValue>
              <StatLabel>{stat.label}</StatLabel>
            </StatContent>
          </StatCard>
        ))}
      </StatsGrid>

      <ChartsGrid>
        <ChartCard>
          <ChartTitle>Доходы по месяцам</ChartTitle>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="revenue" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard>
          <ChartTitle>Рост пользователей</ChartTitle>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={userData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="users" stroke="#6366f1" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </ChartsGrid>
    </Container>
  );
}

export default Analytics;
